/**
 * Sound Effect (SE) Generation System
 *
 * This module generates chiptune sound effects using a template-based approach.
 * Unlike BGM (which uses motif-based composition), SE generation samples parameters
 * from predefined ranges to create one-shot sound events.
 *
 * ## Why Template-Based SE Generation?
 *
 * Sound effects require different design constraints than music:
 * - **Short duration**: SEs are 0.02-1.2 seconds (vs BGM's multi-minute compositions)
 * - **Parameter variety**: Same SE type needs multiple variations (e.g., different jump pitches)
 * - **Tight timing**: SE must trigger precisely on game events without rhythmic quantization
 * - **Isolated playback**: SEs don't need harmonic relationships with BGM
 *
 * Templates define parameter ranges (pitch, duration, envelope) that are sampled at
 * generation time, creating variety without hand-authoring hundreds of discrete SE motifs.
 *
 * ## SE Types and Template Architecture
 *
 * 10 SE types with 4 template variations each (40 templates total):
 * - **jump**: Rising pitch sweep (exponential curve)
 * - **coin**: 2-3 note ascending arpeggio
 * - **explosion**: Noise burst + descending bass sweep
 * - **hit**: Short descending arpeggio (damage sound)
 * - **powerup**: 4-note ascending arpeggio
 * - **laser**: Fast descending sweep
 * - **select**: Short single tone (menu selection)
 * - **click**: Very short high-pitched tone
 * - **synth**: Sustained tone with duty cycle variation
 * - **tone**: Basic single tone
 *
 * Each template specifies:
 * - Channel usage (square1, square2, triangle, noise)
 * - Duration range (min/max in seconds)
 * - Pitch ranges (start/end for sweeps, or fixed pitch)
 * - Envelope type (percussive vs sustained)
 * - Optional note sequences (arpeggios)
 * - Optional pitch sweep configuration
 *
 * ## Seed-Driven Reproducibility
 *
 * Like BGM generation, SE generation is deterministic:
 * - Same seed → same parameter sampling → identical SE
 * - Allows "replay" of exact SE variations for debugging or user favorites
 * - Uses linear congruential generator (LCG) matching BGM's RNG
 *
 * ## Parameter Concretization
 *
 * Template ranges are sampled to concrete values:
 * - **Duration**: Sample from [minDur, maxDur] range
 * - **Pitch**: Sample MIDI note from {min, max} range (discrete)
 * - **Velocity**: Sample from velocity range (quantized to MIDI 0-127)
 * - **Duty cycle**: Sample from continuous range or discrete options
 * - **Envelope**: Fixed per template (percussive or sustained)
 *
 * This generates infinite SE variations from a small template library.
 */

import type { Event, Channel } from "../types.js";
import type { SEType, SETemplate, SEGenerationOptions, SEGenerationResult } from "./seTypes.js";
import { loadSETemplates } from "./seTemplates.js";
import { midiToFrequency, frequencyToSemitones, quantizeMidiToChord } from "../musicUtils.js";
import { VELOCITY_CHANNEL_SCALE } from "../constants/velocity-config.js";

/**
 * Seeded random number generator using Linear Congruential Generator (LCG).
 *
 * Uses the same algorithm as BGM generation for consistency:
 * - Multiplier: 1664525 (from Numerical Recipes)
 * - Increment: 1013904223
 * - Modulus: 2^32 (implicit via unsigned 32-bit overflow)
 *
 * @param seed - Initial seed value (0 is replaced with 1 to avoid degenerate case)
 * @returns Function that returns next random value in [0, 1)
 */
function seedRandom(seed: number): () => number {
  let state = seed >>> 0;
  if (state === 0) {
    state = 1;
  }
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

export class SEGenerator {
  private templates: SETemplate[];

  constructor() {
    this.templates = loadSETemplates();
  }

  /**
   * Generates a sound effect based on type and optional template selection.
   *
   * The generation process:
   * 1. Select template (specific templateId or random from type)
   * 2. Sample parameters from template ranges (duration, pitch, velocity, etc.)
   * 3. Generate event list (noteOn/noteOff pairs with sampled parameters)
   * 4. Return events + metadata for playback
   *
   * @param options - SE generation options (type, seed, templateId, startTime)
   * @returns SE result with events, metadata, and replay options
   */
  generateSE(options: SEGenerationOptions): SEGenerationResult {
    const seed = options.seed ?? Math.floor(Math.random() * 1e9);
    const rng = seedRandom(seed);

    // 1. テンプレート選択
    const template = this.selectTemplate(options, rng);

    // 2. パラメータ具体化
    const params = this.concretizeParameters(
      template,
      rng,
      options.baseFrequency,
      options.quantizeToChord,
      options.velocityScale ?? 1
    );

    // 3. イベントリスト生成
    const events = this.generateEvents(template, params, options.startTime ?? 0.0);
    const duration = this.calculateEventDuration(events, options.startTime ?? 0.0, params.duration);

    const replayOptions: SEGenerationOptions = {
      type: options.type,
      seed,
      startTime: options.startTime ?? 0.0
    };
    if (options.templateId) {
      replayOptions.templateId = options.templateId;
    }
    if (options.baseFrequency !== undefined) {
      replayOptions.baseFrequency = options.baseFrequency;
    }
    if (options.quantizeToChord !== undefined) {
      replayOptions.quantizeToChord = options.quantizeToChord;
    }
    if (options.variantIntent !== undefined) {
      replayOptions.variantIntent = options.variantIntent;
    }
    if (options.velocityScale !== undefined) {
      replayOptions.velocityScale = options.velocityScale;
    }

    return {
      events,
      meta: {
        type: options.type,
        templateId: template.id,
        seed,
        duration,
        channels: template.channels,
        replayOptions
      }
    };
  }

  private selectTemplate(options: SEGenerationOptions, rng: () => number): SETemplate {
    // templateId が指定されていれば強制選択
    if (options.templateId) {
      const template = this.templates.find(t => t.id === options.templateId);
      if (!template) throw new Error(`Template not found: ${options.templateId}`);
      return template;
    }

    // type に合致するテンプレートからランダム選択
    let candidates = this.templates.filter(t => t.type === options.type);
    if (candidates.length === 0) {
      throw new Error(`No templates found for type: ${options.type}`);
    }

    if (options.variantIntent) {
      const taggedCandidates = candidates.filter(t => t.tags?.includes(options.variantIntent!));
      if (taggedCandidates.length > 0) {
        candidates = taggedCandidates;
      }
    }

    return this.weightedTemplateChoice(candidates, rng);
  }

  private concretizeParameters(
    template: SETemplate,
    rng: () => number,
    baseFrequency?: number,
    quantizeToChord?: string,
    velocityScale = 1
  ) {
    const [minDur, maxDur] = template.durationRange;
    const baseDuration = minDur + rng() * (maxDur - minDur);

    const channelParams: Record<Channel, any> = {} as any;
    let maxStartOffset = 0;

    // Calculate pitch shift if baseFrequency is provided
    // Pattern A: Shift all pitches to align the reference pitch with baseFrequency
    let pitchShift = 0;
    if (baseFrequency !== undefined) {
      pitchShift = this.calculatePitchShift(template, baseFrequency);
    }

    for (const ch of template.channels) {
      const tplParams = template.channelParams[ch];
      if (!tplParams) continue;
      const isPitchedSquare = ch === "square1" || ch === "square2";
      const baseVelocityRange: [number, number] = tplParams.velocityRange
        ? tplParams.velocityRange
        : ch === "noise"
          ? [88, 118]
          : [96, 122];
      // Channel-specific scale: square -20%, noise -15%, triangle unchanged
      const chVelocityScale = ch === "square1" || ch === "square2"
        ? 0.8
        : ch === "noise"
          ? 0.85
          : 1.0;
      const sampledVelocity = Math.round(
        Math.max(1, Math.min(127, this.sampleFloatRange(baseVelocityRange, rng) * velocityScale * chVelocityScale))
      );
      const startOffset = tplParams.startOffsetRange
        ? this.sampleFloatRange(tplParams.startOffsetRange, rng)
        : 0;
      maxStartOffset = Math.max(maxStartOffset, startOffset);

      // Apply pitch shift to sampled pitches
      const sampledPitchStart = tplParams.pitchStart
        ? this.samplePitchRange(tplParams.pitchStart, rng)
        : undefined;
      const sampledPitchEnd = tplParams.pitchEnd
        ? this.samplePitchRange(tplParams.pitchEnd, rng)
        : undefined;

      channelParams[ch] = {
        pitchStart: sampledPitchStart !== undefined
          ? this.resolvePitch(sampledPitchStart, pitchShift, quantizeToChord)
          : undefined,
        pitchEnd: sampledPitchEnd !== undefined
          ? this.resolvePitch(sampledPitchEnd, pitchShift, quantizeToChord)
          : undefined,
        dutyCycle: tplParams.dutyCycleRange
          ? this.sampleFloatRange([tplParams.dutyCycleRange.min, tplParams.dutyCycleRange.max], rng)
          : tplParams.dutyCycle
            ? tplParams.dutyCycle[Math.floor(rng() * tplParams.dutyCycle.length)]
            : undefined,
        noiseMode: tplParams.noiseMode,
        envelope: tplParams.envelope,
        velocity: sampledVelocity,
        startOffset,
        releaseSeconds: tplParams.releaseRange
          ? this.sampleFloatRange(tplParams.releaseRange, rng)
          : undefined
      };

      if (template.pitchSweep?.enabled) {
        const sweepCurveOptions = template.pitchSweep.curveOptions ??
          (template.pitchSweep.curveType ? [template.pitchSweep.curveType] : undefined);
        if (sweepCurveOptions) {
          // Use weighted choice if curveWeights are provided
          channelParams[ch].sweepCurve = this.weightedChoice(
            sweepCurveOptions,
            template.pitchSweep.curveWeights,
            rng
          ) ?? template.pitchSweep.curveType;
        }
        if (template.pitchSweep.durationRange) {
          channelParams[ch].sweepDuration = this.sampleFloatRange(template.pitchSweep.durationRange, rng);
        }
      }
    }

    return { duration: baseDuration + maxStartOffset, baseDuration, channelParams };
  }

  private samplePitchRange(range: { min: number; max: number }, rng: () => number): number {
    return Math.floor(range.min + rng() * (range.max - range.min + 1));
  }

  private sampleFloatRange(range: [number, number], rng: () => number): number {
    return range[0] + rng() * (range[1] - range[0]);
  }

  /**
   * Select an option from an array using weighted probabilities.
   * @param options - Array of options to choose from
   * @param weights - Record mapping option string to weight (default: equal weights)
   * @param rng - Random number generator
   * @returns Selected option
   */
  private weightedChoice<T>(
    options: T[],
    weights: Record<string, number> | undefined,
    rng: () => number
  ): T {
    if (!weights) {
      // No weights specified, use uniform random selection
      return options[Math.floor(rng() * options.length)];
    }

    const totalWeight = options.reduce((sum, opt) => sum + (weights[String(opt)] || 1), 0);
    let random = rng() * totalWeight;

    for (const opt of options) {
      const weight = weights[String(opt)] || 1;
      if (random < weight) return opt;
      random -= weight;
    }

    return options[options.length - 1];
  }

  private weightedTemplateChoice(templates: SETemplate[], rng: () => number): SETemplate {
    const totalWeight = templates.reduce((sum, template) => sum + (template.weight ?? 1), 0);
    let random = rng() * totalWeight;

    for (const template of templates) {
      const weight = template.weight ?? 1;
      if (random < weight) return template;
      random -= weight;
    }

    return templates[templates.length - 1];
  }

  private calculateEventDuration(events: Event[], startTime: number, fallbackDuration: number): number {
    if (events.length === 0) {
      return fallbackDuration;
    }
    const lastEventTime = events.reduce((maxTime, event) => Math.max(maxTime, event.time), startTime);
    return Math.max(0, lastEventTime - startTime);
  }

  private generateEvents(
    template: SETemplate,
    params: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];

    for (const ch of template.channels) {
      const chParams = params.channelParams[ch];
      const channelEvents = this.generateChannelEvents(
        ch,
        template,
        chParams,
        params,
        startTime + (chParams.startOffset ?? 0)
      );
      events.push(...channelEvents);
    }

    // time でソート
    events.sort((a, b) => a.time - b.time);

    return events;
  }

  /**
   * チャンネル別にイベントを生成
   */
  private generateChannelEvents(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    if (channel.startsWith("square")) {
      return this.generateSquareEvents(channel, template, chParams, globalParams, startTime);
    } else if (channel === "triangle") {
      return this.generateTriangleEvents(channel, template, chParams, globalParams, startTime);
    } else if (channel === "noise") {
      return this.generateNoiseEvents(template, chParams, globalParams, startTime);
    }
    return [];
  }

  /**
   * Square チャンネルのイベント生成
   */
  private generateSquareEvents(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];

    // デューティサイクル設定
    if (chParams.dutyCycle !== undefined) {
      events.push({
        time: startTime,
        channel,
        command: "setParam",
        data: { param: "duty", value: chParams.dutyCycle }
      });
    }

    // ノートシーケンス（アルペジオ等）
    if (template.noteSequence) {
      events.push(...this.generateNoteSequence(channel, template, chParams, startTime));
    }
    // NES ハードウェアスウィープ（APU sweep unit）
    else if (template.hardwareSweep?.enabled) {
      events.push(...this.generateHardwareSweep(channel, template, chParams, globalParams, startTime));
    }
    // ソフトウェアピッチスイープ（pitchBend補間）
    else if (template.pitchSweep?.enabled) {
      events.push(...this.generatePitchSweep(channel, template, chParams, globalParams, startTime));
    }
    // 単純なノート
    else if (chParams.pitchStart !== undefined) {
      events.push(...this.generateSimpleNote(channel, chParams, globalParams.baseDuration, startTime));
    }

    return events;
  }

  /**
   * Triangle チャンネルのイベント生成
   */
  private generateTriangleEvents(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];

    // Triangle は square と同様の処理（デューティサイクル設定は不要）
    if (template.noteSequence) {
      events.push(...this.generateNoteSequence(channel, template, chParams, startTime));
    } else if (template.pitchSweep?.enabled) {
      events.push(...this.generatePitchSweep(channel, template, chParams, globalParams, startTime));
    } else if (chParams.pitchStart !== undefined) {
      events.push(...this.generateSimpleNote(channel, chParams, globalParams.baseDuration, startTime));
    }

    return events;
  }

  /**
   * Noise チャンネルのイベント生成
   */
  private generateNoiseEvents(
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];
    const channel: Channel = "noise";

    // ノイズモード設定
    if (chParams.noiseMode) {
      events.push({
        time: startTime,
        channel,
        command: "setParam",
        data: { param: "noiseMode", value: chParams.noiseMode }
      });
    }

    const velocity = chParams.velocity ?? 110;
    const releaseSeconds = chParams.releaseSeconds ?? globalParams.baseDuration * 0.3;
    const noiseData: any = { velocity };

    // noiseMode を設定
    if (chParams.noiseMode) {
      noiseData.noiseMode = chParams.noiseMode;
    }

    Object.assign(
      noiseData,
      this.resolveEnvelopeData(chParams, globalParams.baseDuration, releaseSeconds)
    );

    events.push({
      time: startTime,
      channel,
      command: "noteOn",
      data: noiseData
    });

    events.push({
      time: startTime + globalParams.baseDuration,
      channel,
      command: "noteOff",
      data: {}
    });

    return events;
  }

  /**
   * ノートシーケンス生成（アルペジオ等）
   */
  private generateNoteSequence(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];

    if (!template.noteSequence) return events;

    let currentTime = startTime;
    const basePitch = chParams.pitchStart;
    const velocity = chParams.velocity ?? 110;

    for (let i = 0; i < template.noteSequence.intervals.length; i++) {
      const pitch = basePitch + template.noteSequence.intervals[i];
      const noteDur = template.noteSequence.noteDurations[i];

      events.push({
        time: currentTime,
        channel,
        command: "noteOn",
        data: { midi: pitch, velocity }
      });

      events.push({
        time: currentTime + noteDur,
        channel,
        command: "noteOff",
        data: this.resolveNoteOffData(chParams, noteDur)
      });

      currentTime += noteDur;
    }

    return events;
  }

  /**
   * ピッチスイープ生成
   */
  private generatePitchSweep(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];

    if (!template.pitchSweep?.enabled) return events;

    const sweepDuration = chParams.sweepDuration ?? globalParams.baseDuration;
    const curve = chParams.sweepCurve ?? template.pitchSweep.curveType ?? "exponential";
    const velocity = chParams.velocity ?? 110;

    events.push({
      time: startTime,
      channel,
      command: "noteOn",
      data: { midi: chParams.pitchStart, velocity }
    });

    // Note: Web Audio demo では pitchBend パラメータとして処理
    events.push({
      time: startTime,
      channel,
      command: "setParam",
      data: {
        param: "pitchBend",
        value: chParams.pitchEnd,
        rampDuration: sweepDuration,
        curve
      }
    });

    events.push({
      time: startTime + sweepDuration,
      channel,
      command: "noteOff",
      data: this.resolveNoteOffData(chParams, sweepDuration)
    });

    return events;
  }

  /**
   * NES APU ハードウェアスウィープ SE 生成
   *
   * noteOn 後すぐに setSweep を送り、APU sweep unit を起動する。
   * negate=true ならピッチ上昇（period 縮小）、false なら下降（period 拡大）。
   * period が [8, 0x7FF] を外れるとワークレット側が自動ミュートする。
   * noteOff は duration 後に送り、チャンネルとスウィープ状態を解放する。
   */
  private generateHardwareSweep(
    channel: Channel,
    template: SETemplate,
    chParams: any,
    globalParams: any,
    startTime: number
  ): Event[] {
    const events: Event[] = [];
    if (!template.hardwareSweep?.enabled) return events;

    const hs = template.hardwareSweep;
    const duration = globalParams.baseDuration;
    const velocity = chParams.velocity ?? 110;

    events.push({
      time: startTime,
      channel,
      command: "noteOn",
      data: { midi: chParams.pitchStart, velocity }
    });

    events.push({
      time: startTime,
      channel,
      command: "setParam",
      data: {
        param: "sweep",
        enabled: true,
        period: hs.period,
        shift: hs.shift,
        negate: hs.negate
      }
    });

    // noteOff が sweepEnabled を false にするのでスウィープ停止イベントは不要
    events.push({
      time: startTime + duration,
      channel,
      command: "noteOff",
      data: {}
    });

    return events;
  }

  /**
   * 単純なノート生成（エンベロープのみ）
   */
  private generateSimpleNote(
    channel: Channel,
    chParams: any,
    duration: number,
    startTime: number
  ): Event[] {
    const events: Event[] = [];
    const velocity = chParams.velocity ?? 110;

    events.push({
      time: startTime,
      channel,
      command: "noteOn",
      data: { midi: chParams.pitchStart, velocity }
    });

    events.push({
      time: startTime + duration,
      channel,
      command: "noteOff",
      data: this.resolveNoteOffData(chParams, duration)
    });

    return events;
  }

  /**
   * Calculate pitch shift needed to align template's reference pitch with baseFrequency.
   *
   * Pattern A implementation: Uses the template's start pitch as reference and calculates
   * the semitone offset needed to shift it to the target baseFrequency.
   *
   * Strategy for finding reference pitch per SE type:
   * - pitch sweep (jump, laser): Use pitchStart as reference
   * - note sequence (coin, powerup, hit): Use first note (pitchStart) as reference
   * - single tone (select, click, synth, tone): Use pitchStart as reference
   * - multi-channel (explosion): Use triangle channel pitch (bass) as reference
   *
   * @param template SE template with pitch configuration
   * @param baseFrequency Target frequency in Hz
   * @returns Semitone shift to apply to all pitches
   */
  private calculatePitchShift(template: SETemplate, baseFrequency: number): number {
    // Find the first channel with pitch information to use as reference
    for (const ch of template.channels) {
      const chParams = template.channelParams[ch];
      if (!chParams?.pitchStart) continue;

      // Use the middle of the pitch range as reference
      // This ensures we shift from a representative pitch rather than edge cases
      const referenceMidi = (chParams.pitchStart.min + chParams.pitchStart.max) / 2;
      const referenceFreq = midiToFrequency(referenceMidi);
      const frequencyRatio = baseFrequency / referenceFreq;
      const shiftSemitones = frequencyToSemitones(frequencyRatio);

      // Round to nearest semitone for discrete pitch shifting
      return Math.round(shiftSemitones);
    }

    // No pitch information found, return 0 (no shift)
    return 0;
  }

  /**
   * Clamp MIDI note number to valid range (0-127).
   *
   * Prevents pitch shifting from producing invalid MIDI values.
   * Also ensures we don't exceed Nyquist frequency (sample rate / 2).
   *
   * @param midi MIDI note number (may be out of range)
   * @returns Clamped MIDI note number
   */
  private clampMidi(midi: number): number {
    return Math.max(0, Math.min(127, Math.round(midi)));
  }

  private resolvePitch(midi: number, pitchShift: number, quantizeToChord?: string): number {
    const shiftedMidi = this.clampMidi(midi + pitchShift);
    if (!quantizeToChord) {
      return shiftedMidi;
    }
    return this.clampMidi(quantizeMidiToChord(shiftedMidi, quantizeToChord));
  }

  private resolveNoteOffData(chParams: any, duration: number): { releaseSeconds?: number } {
    const releaseSeconds = this.resolveReleaseSeconds(chParams, duration);
    return releaseSeconds ? { releaseSeconds } : {};
  }

  private resolveEnvelopeData(
    chParams: any,
    duration: number,
    fallbackReleaseSeconds: number
  ): { decaySeconds?: number; releaseSeconds?: number } {
    switch (chParams.envelope) {
      case "percussive":
        return { decaySeconds: duration * 0.7, releaseSeconds: fallbackReleaseSeconds };
      case "pluck":
        return { decaySeconds: Math.min(0.08, duration * 0.45), releaseSeconds: fallbackReleaseSeconds };
      case "snap":
        return { decaySeconds: Math.min(0.025, duration * 0.35), releaseSeconds: fallbackReleaseSeconds };
      case "fade":
        return { decaySeconds: duration, releaseSeconds: fallbackReleaseSeconds };
      default:
        return chParams.releaseSeconds ? { releaseSeconds: fallbackReleaseSeconds } : {};
    }
  }

  private resolveReleaseSeconds(chParams: any, duration: number): number | undefined {
    if (chParams.releaseSeconds) {
      return chParams.releaseSeconds;
    }
    switch (chParams.envelope) {
      case "pluck":
        return Math.min(0.045, duration * 0.35);
      case "snap":
        return Math.min(0.018, duration * 0.25);
      case "fade":
        return Math.min(0.18, duration * 0.6);
      case "percussive":
        return Math.min(0.06, duration * 0.3);
      default:
        return undefined;
    }
  }
}
